import { ArrowRight, ShieldCheck, Headset, Package, LifeBuoy } from "lucide-react";
import { SectionLabel } from "@/components/yanre/SectionLabel";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { track } from "@/lib/track";
import { useLocale } from "@/i18n/LocaleContext";

const FLOW = ["Factory", "Export", "Ocean Freight", "Destination Port"];

export const Logistics = ({ index, onCta }) => {
  const { t } = useLocale();
  return (
    <section className="section-dark relative overflow-hidden border-t border-carbon-700" data-testid="logistics-section">
      <div className="grid-lines absolute inset-0 opacity-40" />
      <div className="container-edge relative py-16 lg:py-24">
        <Reveal>
          <SectionLabel index={index}>International Logistics</SectionLabel>
        </Reveal>
        <h2 className="mt-8 font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <RevealLines
            lines={[
              t("lg.h1a", "Your equipment. Your destination."),
              <span key="c">
                {t("lg.h1b", "Your choice.")}
              </span>,
            ]}
          />
        </h2>
        <div className="mt-12 grid gap-px lg:grid-cols-2" data-testid="logistics-options">
          {[
            { num: "01", key: "lg.o1", title: "Use Your Logistics Partner", body: "Work with the logistics provider you already trust. We prepare your order for export and coordinate with your partner." },
            { num: "02", key: "lg.o2", title: "Let Us Coordinate It", body: "Request YANRE logistics coordination according to the contracted scope — from factory to destination port." },
          ].map((o, i) => (
            <Reveal key={o.num} delay={i * 0.08}>
              <div className="h-full border border-carbon-700 bg-carbon-900 p-7 sm:p-9">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-signal">{t("lg.option", "Option")} {o.num}</span>
                <h3 className="mt-4 font-display text-2xl font-extrabold uppercase leading-tight text-white sm:text-3xl">{t(`${o.key}.t`, o.title)}</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-titanium/60 sm:text-base">{t(`${o.key}.b`, o.body)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.12}>
          <div className="mt-10 flex flex-wrap items-center gap-y-4 border border-carbon-700 bg-carbon-800 p-6 sm:p-7" data-testid="logistics-flow">
            {FLOW.map((f, i) => (
              <span key={f} className="flex items-center">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-titanium">{t(`lg.f${i + 1}`, f)}</span>
                {i < FLOW.length - 1 && <ArrowRight className="mx-4 h-4 w-4 text-signal rtl:-scale-x-100" />}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-titanium/40">
            {t("lg.disclaimer", "Customs clearance, taxes, duties and final-mile services are included only when explicitly contracted.")}
          </p>
          <button
            type="button"
            onClick={() => {
              track("cta_start_project", { placement: "logistics" });
              onCta?.();
            }}
            className="btn-ghost group mt-7 border-titanium/30 text-titanium hover:border-signal hover:text-white"
            data-testid="logistics-cta-btn"
          >
            {t("lg.cta", "Include Logistics in My Project")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

const AFTERSALES = [
  { key: "as.1", icon: ShieldCheck, title: "Factory-Backed Warranty", body: "Manufacturer warranty support for your equipment." },
  { key: "as.2", icon: Headset, title: "Technical Support", body: "Direct technical assistance connected to the people behind the equipment." },
  { key: "as.3", icon: Package, title: "Parts Support", body: "Manufacturer-level parts support for your machines." },
  { key: "as.4", icon: LifeBuoy, title: "After-Sales Assistance", body: "Ongoing after-sales assistance after delivery." },
];

export const AfterSales = ({ index }) => {
  const { t } = useLocale();
  return (
    <section className="section-darker relative overflow-hidden border-t border-carbon-700" data-testid="after-sales-section">
      <div className="container-edge relative py-16 lg:py-24">
        <Reveal>
          <SectionLabel index={index}>After-Sales &amp; Warranty</SectionLabel>
        </Reveal>
        <h2 className="mt-8 max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <RevealLines
            lines={[
              t("as.h1a", "The relationship doesn't end"),
              <span key="w">
                {t("as.h1b", "when the container ships.")}
              </span>,
            ]}
          />
        </h2>
        <div className="mt-12 grid gap-px sm:grid-cols-2 lg:grid-cols-4" data-testid="after-sales-grid">
          {AFTERSALES.map((a, i) => (
            <Reveal key={a.key} delay={i * 0.05}>
              <div className="flex h-full flex-col border border-carbon-700 bg-carbon-900 p-6 sm:p-7">
                <a.icon className="h-7 w-7 text-signal" />
                <h3 className="mt-5 font-display text-lg font-bold uppercase leading-tight text-white">{t(`${a.key}.t`, a.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-titanium/60">{t(`${a.key}.b`, a.body)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
